import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/SupabaseAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  FileText,
  Activity,
  TrendingUp,
  Upload,
  ArrowRight,
  Calendar,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { apiFetch } from '../lib/api';
import { getDashboardStats } from '../lib/patients';

export const DashboardPage = () => {
  const { organization, user, isDemo } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await getDashboardStats(organization.id);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error(error.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [organization.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await apiFetch(`/export/data/${format}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `pranagen_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // Handle ISO datetime strings (with T) and date-only strings
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-1">
            Here's an overview of your patients' biological age assessments.
          </p>
        </div>
        {!isDemo && (
          <Link to="/upload">
            <Button
              className="bg-[#196a70] hover:bg-[#12565b] text-white shadow-sm"
              data-testid="new-analysis-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white border-slate-200 shadow-soft card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Patients</p>
                {loading ? (
                  <Skeleton className="h-9 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold font-heading text-slate-900 mt-1">
                    {stats?.patient_count || 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#dbe9e5] flex items-center justify-center">
                <Users className="w-6 h-6 text-[#196a70]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-soft card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Analyses</p>
                {loading ? (
                  <Skeleton className="h-9 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold font-heading text-slate-900 mt-1">
                    {stats?.analysis_count || 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#dbe9e5] flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#196a70]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-soft card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Active Tracking</p>
                {loading ? (
                  <Skeleton className="h-9 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold font-heading text-slate-900 mt-1">
                    {stats?.patient_count || 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#dbe9e5] flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#196a70]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Analyses */}
      <Card className="bg-white border-slate-200 shadow-soft">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-slate-900">
              Recent Analyses
            </CardTitle>
            <Link to="/analyses">
              <Button variant="ghost" className="text-[#196a70]" data-testid="view-all-analyses-btn">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : stats?.recent_analyses?.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {stats.recent_analyses.map((analysis) => (
                <div
                  key={analysis.analysis_id}
                  className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                    <Activity className="w-5 h-5 text-[#196a70]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {analysis.patient_name || 'Unknown Patient'}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(analysis.collection_date)}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">
                      Pranagen Age: {analysis.pranagen_clock?.toFixed(1) || '—'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Chrono: {analysis.chronological_age?.toFixed(1)}
                    </p>
                  </div>
                  <Link to={`/analyses/${analysis.analysis_id}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#196a70]"
                      data-testid={`view-analysis-${analysis.analysis_id}`}
                    >
                      View
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-medium text-slate-900 mb-1">No analyses yet</h3>
              <p className="text-slate-500 text-sm mb-4">
                {isDemo ? 'No analyses to show yet.' : 'Upload your first blood work PDF to get started.'}
              </p>
              {!isDemo && (
                <Link to="/upload">
                  <Button className="bg-[#196a70] hover:bg-[#12565b] text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Blood Work
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-white to-[#dbe9e5] border-slate-200 shadow-soft card-hover">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#196a70] flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-slate-900 mb-1">
                  Manage Patients
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Add, edit, or view patient profiles and their history.
                </p>
                <Link to="/patients">
                  <Button
                    variant="outline"
                    className="border-[#196a70] text-[#196a70] hover:bg-[#196a70] hover:text-white"
                    data-testid="manage-patients-btn"
                  >
                    View Patients
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {!isDemo && (
          <Card className="bg-gradient-to-br from-white to-[#dbe9e5] border-slate-200 shadow-soft card-hover">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#196a70] flex items-center justify-center flex-shrink-0">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-slate-900 mb-1">
                    New Analysis
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Upload a blood work PDF for AI-powered extraction and analysis.
                  </p>
                  <Link to="/upload">
                    <Button
                      variant="outline"
                      className="border-[#196a70] text-[#196a70] hover:bg-[#196a70] hover:text-white"
                      data-testid="start-analysis-btn"
                    >
                      Start Analysis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Export Data Section */}
      <Card className="bg-white border-slate-200 shadow-soft">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
              <Download className="w-5 h-5 text-[#196a70]" />
            </div>
            <div>
              <CardTitle className="font-heading text-xl text-slate-900">
                Export Data
              </CardTitle>
              <p className="text-sm text-slate-500">Download your data in CSV or Excel format</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-slate-600 mb-4">
            Download everything in a single file. <strong>Excel</strong> has two sheets — Patients
            (name, ID, date of birth, sex) and Biological Age Analyses (ages, mortality risk, and all
            blood markers). <strong>CSV</strong> is one combined table.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exporting === 'csv'}
              className="flex-1"
              data-testid="export-data-csv"
            >
              {exporting === 'csv' ? (
                <span className="animate-pulse">Exporting...</span>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Download CSV
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('excel')}
              disabled={exporting === 'excel'}
              className="flex-1"
              data-testid="export-data-excel"
            >
              {exporting === 'excel' ? (
                <span className="animate-pulse">Exporting...</span>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Excel
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
